import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { TermEntry } from '../../types/mentioned-terms';
import { MoreItemsPill } from './more-items-pill';
import * as styles from './pill.scss';
import { Pill } from './pill';
import { h } from 'preact';

const MORE_BUTTON_WIDTH = 30;
const PILLS_INLINE_MARGIN = 6;

interface Props {
  termEntries: TermEntry[];
  onTermClick: (term: string) => void;
  activeTerm: string | null;
}
export const PillsRow = ({ termEntries, onTermClick, activeTerm }: Props): h.JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleTerms, setVisibleTerms] = useState<TermEntry[]>([]);
  const [hiddenTerms, setHiddenTerms] = useState<TermEntry[]>([]);

  const calculateVisibleButtons = useCallback((): void => {
    if (!containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    let currentWidth = 0;
    const measuringElement = document.createElement('div');
    measuringElement.style.position = 'fixed';
    measuringElement.style.left = '-99999px';
    measuringElement.style.visibility = 'hidden';
    document.body.appendChild(measuringElement);
    const visible: TermEntry[] = [];
    const hidden: TermEntry[] = [];

    let maxWidthExceeded = false;
    termEntries.forEach((termEntry) => {
      if (maxWidthExceeded) {
        hidden.push(termEntry);
        return;
      }

      const [termText, termCount] = termEntry;

      const button = document.createElement('button');
      button.className = styles.pill;
      button.textContent = `${termText} (${termCount})`;

      measuringElement.appendChild(button);

      const buttonWidth = button.offsetWidth + PILLS_INLINE_MARGIN;

      if (currentWidth + buttonWidth + MORE_BUTTON_WIDTH >= containerWidth) {
        maxWidthExceeded = true;
        hidden.push(termEntry);
        return;
      }

      currentWidth += buttonWidth;
      visible.push(termEntry);
    });

    document.body.removeChild(measuringElement);

    setVisibleTerms(visible);
    setHiddenTerms(hidden);
  }, [termEntries]);

  useEffect(() => {
    calculateVisibleButtons();

    const resizeObserver = new ResizeObserver(calculateVisibleButtons);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return (): void => {
      resizeObserver.disconnect();
    };
  }, [calculateVisibleButtons]);

  return (
    <div ref={containerRef}>
      {visibleTerms.map((term) => {
        const [termText, termCount] = term;
        return (
          <Pill
            key={termText}
            isActive={termText.toLowerCase() === activeTerm?.toLowerCase()}
            text={`${termText} (${termCount})`}
            onClick={() => {
              onTermClick(termText);
            }}
          />
        );
      })}
      {hiddenTerms.length > 0 ? <MoreItemsPill activeTerm={activeTerm} terms={hiddenTerms} onTermClick={onTermClick} /> : null}
    </div>
  );
};
